import type {
  AchievementTier,
  BadgeGlyphId,
  EngagementMetric,
  EngagementUnlock,
} from '@scrambl/shared'
import {
  formatMetricAmount,
  getAchievementDefinition,
  getEngagementMetricMeta,
} from '@scrambl/shared'

export interface UnlockPresentation {
  id: string
  kind: EngagementUnlock['kind']
  /** Small pill above the title ("Gold badge", "Weekly goal"). */
  eyebrow: string
  /** Four words or fewer. */
  title: string
  /** Eight words or fewer. */
  line: string
  glyph: BadgeGlyphId
  tier: AchievementTier
  /** The celebratory dismiss label. */
  cta: string
}

const TIER_EYEBROW: Record<AchievementTier, string> = {
  bronze: 'Bronze badge',
  silver: 'Silver badge',
  gold: 'Gold badge',
}

const CTAS = [
  'Woohoo!',
  'Heck yes!',
  "Let's go!",
  'High five!',
  'Keep it rolling',
]

/** Stable per unlock, so a re-render never swaps the label mid-moment. */
function pick<T>(options: readonly T[], seed: string): T {
  let hash = 0
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return options[hash % options.length]
}

/** Copy and art for an unlock, or null for a key this build doesn't know. */
export function presentUnlock(
  unlock: EngagementUnlock,
): UnlockPresentation | null {
  const cta = pick(CTAS, unlock.id)

  if (unlock.kind === 'goal') {
    const metric = unlock.key as EngagementMetric
    const meta = getEngagementMetricMeta(metric)
    const target = Number(unlock.context?.target ?? 0)
    return {
      id: unlock.id,
      kind: 'goal',
      eyebrow: 'Weekly goal',
      title: 'Goal smashed',
      line: target
        ? `${formatMetricAmount(metric, target)} this week.`
        : `${meta.label} goal done this week.`,
      glyph: meta.glyph,
      tier: 'gold',
      cta,
    }
  }

  const def = getAchievementDefinition(unlock.key)
  if (!def) return null

  if (def.rule.kind === 'streak') {
    const days = def.rule.days
    return {
      id: unlock.id,
      kind: 'achievement',
      eyebrow: TIER_EYEBROW[def.tier],
      title: `${days}-day streak`,
      line: `${def.name}. Keep the flame going.`,
      glyph: def.glyph,
      tier: def.tier,
      cta,
    }
  }

  return {
    id: unlock.id,
    kind: 'achievement',
    eyebrow: TIER_EYEBROW[def.tier],
    title: def.name,
    line: def.description,
    glyph: def.glyph,
    tier: def.tier,
    cta,
  }
}

/** Oldest unseen unlock that hasn't been dismissed in this session. */
export function nextUnlock(
  unseen: readonly EngagementUnlock[],
  dismissed: ReadonlySet<string>,
): EngagementUnlock | null {
  return (
    [...unseen]
      .filter(unlock => !dismissed.has(unlock.id))
      .sort((a, b) => a.earnedAt.localeCompare(b.earnedAt))[0] ?? null
  )
}

/** Dev-only synthetic unlock from `?preview-unlock=streak_7|goal:TASKS_COMPLETED|<achievement key>`. */
export function previewUnlockFromParam(
  value: string | null,
): EngagementUnlock | null {
  if (!value) return null
  const earnedAt = new Date().toISOString()
  if (value.startsWith('goal:')) {
    return {
      id: `preview:${value}`,
      kind: 'goal',
      key: value.slice('goal:'.length),
      earnedAt,
      context: { target: 12 },
    }
  }
  const key = value.startsWith('achievement:')
    ? value.slice('achievement:'.length)
    : value
  if (!getAchievementDefinition(key)) return null
  return { id: `preview:${value}`, kind: 'achievement', key, earnedAt }
}
